import type { ChildActivityLevel } from "@sytofood/shared";

import type { Lang } from "./index";

// Copy for child profiles (ADR-0018). Each language is written natively
// (grammar, idiom, tone), not literally translated. Interpolation goes
// through function fields. TS strict + the Record<Lang, ChildrenCopy>
// shape catches missing keys across PL/EN/DE.

export type ChildrenCopy = {
  // --- shared / switcher / common ---
  chProfileSelf: string;
  chSwitcherTitle: string;
  chSwitcherAria: string;
  chSwitcherOpen: string;
  chAddChild: string;
  chAddChildShort: string;
  // --- screen frame ---
  chScreenBack: string;
  chScreenTitle: string;
  chScreenHeading: string;
  chScreenIntro: string;
  chSectionEyebrow: string;
  chSectionEyebrowDash: string;
  chDashSectionHint: (n: number) => string;
  chDashOpen: string;
  // --- list ---
  chListEmpty: string;
  chListEmptyHint: string;
  chRowAgeYears: (n: number) => string;
  chRowKcalApprox: (n: number) => string;
  chTargetIncomplete: string;
  chTargetIncompleteAge: string;
  // --- form fields ---
  chFormName: string;
  chFormNamePlaceholder: string;
  chFormDob: string;
  chFormSex: string;
  chFormSexMale: string;
  chFormSexFemale: string;
  chFormHeight: string;
  chFormHeightPlaceholder: string;
  chFormWeight: string;
  chFormWeightPlaceholder: string;
  chFormActivity: string;
  chFormOverride: string;
  chFormOverridePlaceholder: string;
  chFormOverrideHint: string;
  chActivityOptions: Record<ChildActivityLevel, string>;
  chActivityNone: string;
  // --- actions ---
  chSave: string;
  chSaving: string;
  chCancel: string;
  chEdit: string;
  chCollapse: string;
  chDelete: string;
  chDeleting: string;
  chDeleteConfirm: (name: string) => string;
  chDeleteConfirmCta: string;
  chDeleteCancel: string;
  // --- errors ---
  chErrName: string;
  chErrDob: string;
  chErrSex: string;
  chErrGeneric: string;
  // --- daily tracker child-mode banner ---
  chTargetBanner: string;
  chTargetBannerCta: string;
  chMealsBannerSelf: string;
};

const pl: ChildrenCopy = {
  chProfileSelf: "Ja",
  chSwitcherTitle: "Czyj dziennik",
  chSwitcherAria: "Wybierz profil dziennika",
  chSwitcherOpen: "Otwórz wybór profilu",
  chAddChild: "Dodaj dziecko",
  chAddChildShort: "Dodaj dziecko +",
  chScreenBack: "Wróć",
  chScreenTitle: "Profile rodzinne",
  chScreenHeading: "Twoje dzieci",
  chScreenIntro: "Każde dziecko ma własny limit kalorii i osobny dziennik posiłków.",
  chSectionEyebrow: "Profile rodzinne",
  chSectionEyebrowDash: "Rodzina",
  chDashSectionHint: (n) => {
    if (n === 0) return "Brak dodanych dzieci";
    if (n === 1) return "Masz 1 profil dziecka";
    if (n >= 2 && n <= 4) return `Masz ${n} profile dzieci`;
    return `Masz ${n} profili dzieci`;
  },
  chDashOpen: "Przejdź do profili",
  chListEmpty: "Brak dodanych profili",
  chListEmptyHint: "Dodaj pierwsze dziecko, żeby śledzić jego posiłki osobno.",
  chRowAgeYears: (n) => {
    if (n === 0) return "Mniej niż rok";
    if (n === 1) return "1 rok";
    if (n >= 2 && n <= 4) return `${n} lata`;
    return `${n} lat`;
  },
  chRowKcalApprox: (n) => `~ ${n} kcal`,
  chTargetIncomplete: "Uzupełnij wzrost i wagę",
  chTargetIncompleteAge: "Wiek poza zakresem 3 do 17 lat",
  chFormName: "Imię",
  chFormNamePlaceholder: "np. Hania",
  chFormDob: "Data urodzenia",
  chFormSex: "Płeć biologiczna",
  chFormSexMale: "Chłopiec",
  chFormSexFemale: "Dziewczynka",
  chFormHeight: "Wzrost (cm)",
  chFormHeightPlaceholder: "np. 120",
  chFormWeight: "Waga (kg)",
  chFormWeightPlaceholder: "np. 28",
  chFormActivity: "Aktywność",
  chFormOverride: "Własny limit kcal",
  chFormOverridePlaceholder: "puste = wyliczone",
  chFormOverrideHint: "Wpisz tylko jeśli chcesz nadpisać szacunek.",
  chActivityOptions: {
    sedentary: "Siedzący tryb",
    light: "Lekko aktywne",
    moderate: "Średnio aktywne",
    high: "Bardzo aktywne",
    "very-high": "Wyczynowo aktywne",
  },
  chActivityNone: "Nie wybrano",
  chSave: "Zapisz",
  chSaving: "Zapisuję...",
  chCancel: "Anuluj",
  chEdit: "Edytuj",
  chCollapse: "Zwiń",
  chDelete: "Usuń",
  chDeleting: "Usuwam...",
  chDeleteConfirm: (name) => `Usunąć profil ${name}?`,
  chDeleteConfirmCta: "Tak, usuń",
  chDeleteCancel: "Cofnij",
  chErrName: "Podaj imię dziecka.",
  chErrDob: "Wpisz datę urodzenia.",
  chErrSex: "Wybierz płeć biologiczną.",
  chErrGeneric: "Nie udało się zapisać. Spróbuj jeszcze raz.",
  chTargetBanner: "Uzupełnij wzrost i wagę, żeby pokazać kcal.",
  chTargetBannerCta: "Uzupełnij dane",
  chMealsBannerSelf: "Widok dziecka. Zmień profil w górnym pasku, jeśli to pomyłka.",
};

const en: ChildrenCopy = {
  chProfileSelf: "Me",
  chSwitcherTitle: "Whose journal",
  chSwitcherAria: "Pick the active journal profile",
  chSwitcherOpen: "Open profile picker",
  chAddChild: "Add a child",
  chAddChildShort: "Add a child +",
  chScreenBack: "Back",
  chScreenTitle: "Family profiles",
  chScreenHeading: "Your children",
  chScreenIntro: "Each child gets their own calorie target and a separate meal log.",
  chSectionEyebrow: "Family profiles",
  chSectionEyebrowDash: "Family",
  chDashSectionHint: (n) => {
    if (n === 0) return "No child profiles yet";
    if (n === 1) return "You have 1 child profile";
    return `You have ${n} child profiles`;
  },
  chDashOpen: "Open profiles",
  chListEmpty: "No profiles yet",
  chListEmptyHint: "Add your first child to track their meals separately.",
  chRowAgeYears: (n) => {
    if (n === 0) return "Under one";
    if (n === 1) return "1 year";
    return `${n} years`;
  },
  chRowKcalApprox: (n) => `~ ${n} kcal`,
  chTargetIncomplete: "Add height and weight",
  chTargetIncompleteAge: "Age outside the 3 to 17 range",
  chFormName: "Name",
  chFormNamePlaceholder: "e.g. Hannah",
  chFormDob: "Date of birth",
  chFormSex: "Biological sex",
  chFormSexMale: "Boy",
  chFormSexFemale: "Girl",
  chFormHeight: "Height (cm)",
  chFormHeightPlaceholder: "e.g. 120",
  chFormWeight: "Weight (kg)",
  chFormWeightPlaceholder: "e.g. 28",
  chFormActivity: "Activity",
  chFormOverride: "Custom kcal target",
  chFormOverridePlaceholder: "empty = estimated",
  chFormOverrideHint: "Only fill in if you want to override the estimate.",
  chActivityOptions: {
    sedentary: "Sedentary",
    light: "Lightly active",
    moderate: "Moderately active",
    high: "Very active",
    "very-high": "Athlete level",
  },
  chActivityNone: "Not selected",
  chSave: "Save",
  chSaving: "Saving...",
  chCancel: "Cancel",
  chEdit: "Edit",
  chCollapse: "Collapse",
  chDelete: "Delete",
  chDeleting: "Deleting...",
  chDeleteConfirm: (name) => `Delete ${name}'s profile?`,
  chDeleteConfirmCta: "Yes, delete",
  chDeleteCancel: "Back",
  chErrName: "Please add a name.",
  chErrDob: "Please add a date of birth.",
  chErrSex: "Please choose a biological sex.",
  chErrGeneric: "Could not save. Please try again.",
  chTargetBanner: "Add height and weight to show kcal.",
  chTargetBannerCta: "Add details",
  chMealsBannerSelf: "Child view. Change the profile in the top bar if this was a mistake.",
};

const de: ChildrenCopy = {
  chProfileSelf: "Ich",
  chSwitcherTitle: "Wessen Tagebuch",
  chSwitcherAria: "Aktives Profil wählen",
  chSwitcherOpen: "Profilauswahl öffnen",
  chAddChild: "Kind hinzufügen",
  chAddChildShort: "Kind hinzufügen +",
  chScreenBack: "Zurück",
  chScreenTitle: "Familienprofile",
  chScreenHeading: "Deine Kinder",
  chScreenIntro: "Jedes Kind hat sein eigenes Kalorienziel und ein separates Mahlzeitentagebuch.",
  chSectionEyebrow: "Familienprofile",
  chSectionEyebrowDash: "Familie",
  chDashSectionHint: (n) => {
    if (n === 0) return "Noch keine Kinderprofile";
    if (n === 1) return "Du hast 1 Kinderprofil";
    return `Du hast ${n} Kinderprofile`;
  },
  chDashOpen: "Profile öffnen",
  chListEmpty: "Noch keine Profile",
  chListEmptyHint: "Füge dein erstes Kind hinzu, um seine Mahlzeiten separat zu erfassen.",
  chRowAgeYears: (n) => {
    if (n === 0) return "Unter einem Jahr";
    if (n === 1) return "1 Jahr";
    return `${n} Jahre`;
  },
  chRowKcalApprox: (n) => `~ ${n} kcal`,
  chTargetIncomplete: "Größe und Gewicht ergänzen",
  chTargetIncompleteAge: "Alter außerhalb von 3 bis 17 Jahren",
  chFormName: "Name",
  chFormNamePlaceholder: "z. B. Hanna",
  chFormDob: "Geburtsdatum",
  chFormSex: "Biologisches Geschlecht",
  chFormSexMale: "Junge",
  chFormSexFemale: "Mädchen",
  chFormHeight: "Größe (cm)",
  chFormHeightPlaceholder: "z. B. 120",
  chFormWeight: "Gewicht (kg)",
  chFormWeightPlaceholder: "z. B. 28",
  chFormActivity: "Aktivität",
  chFormOverride: "Eigenes kcal-Ziel",
  chFormOverridePlaceholder: "leer = geschätzt",
  chFormOverrideHint: "Nur ausfüllen, wenn du die Schätzung überschreiben willst.",
  chActivityOptions: {
    sedentary: "Sitzend",
    light: "Leicht aktiv",
    moderate: "Mäßig aktiv",
    high: "Sehr aktiv",
    "very-high": "Sehr sportlich aktiv",
  },
  chActivityNone: "Nicht gewählt",
  chSave: "Speichern",
  chSaving: "Wird gespeichert...",
  chCancel: "Abbrechen",
  chEdit: "Bearbeiten",
  chCollapse: "Einklappen",
  chDelete: "Löschen",
  chDeleting: "Wird gelöscht...",
  chDeleteConfirm: (name) => `Profil von ${name} löschen?`,
  chDeleteConfirmCta: "Ja, löschen",
  chDeleteCancel: "Zurück",
  chErrName: "Bitte einen Namen eingeben.",
  chErrDob: "Bitte ein Geburtsdatum eingeben.",
  chErrSex: "Bitte das biologische Geschlecht wählen.",
  chErrGeneric: "Speichern fehlgeschlagen. Bitte erneut versuchen.",
  chTargetBanner: "Größe und Gewicht ergänzen, um kcal anzuzeigen.",
  chTargetBannerCta: "Daten ergänzen",
  chMealsBannerSelf:
    "Kinderansicht. Profil oben in der Leiste ändern, falls das ein Versehen war.",
};

export const childrenCopy: Record<Lang, ChildrenCopy> = { pl, en, de };
